/**
 * Adapter: nested `InfluencerCardData` → `InfluencerDetails` for the HOME INFLUENCER_V2 widgets.
 *
 * Builds on the shared PDP IRL adapter (`influencer-section.adapter`) and overlays the fields the
 * home `InfluencerV2Card` renders but the PDP non-DS `InfluencerCard` atom ignores — so the shared
 * adapter deliberately drops them:
 * - `videos` / `posterImage`: v2 cards (`InfluencerSectionWidgetData.version = 2`) carry an
 *   adaptive/HLS Mux source the card plays inline.
 * - `buttons`: the SHOP CTA. `InfluencerV2Card` gates its click-through on `buttons[0]`'s href,
 *   so an empty array would render a dead card.
 * - `showImage`: kept as authored when videos exist (the shared base already normalizes the
 *   no-video case to image).
 */

import InfluencerDetails from '@src/quince-site-common/src/domains/InfluencerDetails'
import Video from '@src/quince-site-common/src/domains/Video'

import { adaptImage, adaptInfluencerCard } from '../influencer-section/influencer-section.adapter'
import { protoButtonToDomain } from '../widget-utils'

import type { InfluencerCardData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * The `Video` atom builds its source as `https:${url}` — it expects the PROTOCOL-RELATIVE URLs the
 * Contentful pipeline stores on the domain (`//videos.ctfassets.net/...`). PL ships the Mux HLS
 * url absolute (`https://stream.mux.com/....m3u8`), which the atom would mangle into
 * `https:https://...` and never load. Normalize to protocol-relative so the atom resolves it
 * exactly like non-PL.
 */
const toProtocolRelative = (url?: string): string => (url ?? '').replace(/^https?:/, '')

// Videos share the images' MediaContent proto shape ({ id, url, alt, contentType, ... }).
const adaptVideo = (video: InfluencerCardData['videos'][number]): Video =>
  new Video({
    id: video.id ?? '',
    url: toProtocolRelative(video.url),
    fileName: '',
    contentType: video.contentType ?? '',
    title: video.alt ?? '',
  })

export const adaptInfluencerV2Card = (card: InfluencerCardData): InfluencerDetails => {
  const showImage = card.videos.length === 0 ? true : card.showImage
  return {
    ...adaptInfluencerCard(card),
    videos: card.videos.map(adaptVideo),
    // The Video atom's poster path (`getPosterImageUrl`) prepends `https:` just like its source —
    // override the shared adapter's absolute url (`withProtocol`) back to protocol-relative.
    posterImage: card.posterImage
      ? { ...adaptImage(card.posterImage), url: toProtocolRelative(card.posterImage.url) }
      : undefined,
    showImage,
    // The shoppable popup (`SocialSectionSlide`) plays the card's video only when
    // `showVideoOnModal === true`; otherwise it falls back to the still image. The proto carries
    // no such field, so honor it if a future BFF ships it (structural read, like `socialPosts`)
    // and default video cards to playing in the modal.
    showVideoOnModal: (card as { showVideoOnModal?: boolean }).showVideoOnModal ?? !showImage,
    buttons: card.buttons.map(button => protoButtonToDomain(button as Record<string, unknown>)),
  }
}
